import { QueryFactory } from '@lmt/shared-types';
import { ResourceDto, ResourceType } from '@lmt/shared-types';

import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ResourcesTable } from './components/ResourcesTable';

import tableStyles from '@lmt/unicorn-shared/app/components/common/table/TableTitle.module.scss';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { useEventCallback } from '@mui/material';
import { useInsertKeyHandler } from '../../dictionaries/useInsertKeyHandler';

interface Props {
  resourceType: ResourceType;
}

function getTranslationScope(type: ResourceType) {
  switch (type) {
    case ResourceType.Places:
      return 'resources.place.list';
    case ResourceType.SpecialConditions:
      return 'resources.special_conditions.list';
    case ResourceType.Doctors:
      return 'resources.doctors.list';
    default:
      throw new Error('Unknown type');
  }
}

const ResourceList: FC<Props> = (props) => {
  const forms = useGlobalForms();
  const translationScope = getTranslationScope(props.resourceType);
  const i18next = useScopedTranslation(translationScope);
  const modals = useModal();
  const queryCache = useQueryClient();

  const resourcesQuery = QueryFactory.ResourceQuery.useGetResourcesQuery({
    includeDeleted: false,
    resourceType: props.resourceType,
  });

  const openEditing = useEventCallback((resource: ResourceDto) => {
    forms.openResourceEditing(resource, i18next.t('edit'));
  });

  const openCreating = useEventCallback((resourceType: ResourceType) => {
    forms.openResourceCreating(resourceType, i18next.t('create'));
  });

  const removeResource = useEventCallback(async (resource: ResourceDto) => {
    const isConfirmed = await modals.showConfirm({
      text: i18next.t('confirm_message_remove'),
    });
    if (isConfirmed) {
      try {
        await QueryFactory.ResourceClient.removeResource(resource.id);
      } catch (error: any) {
        if (error.status === 409) {
          await modals.showError({
            text: i18next.t('resource_is_busy_message'),
          });
        } else {
          await modals.showError({ text: convertToErrorString(error) });
          throw error;
        }
      }
    }
  });

  useInsertKeyHandler(() => openCreating(props.resourceType));

  return (
    <div
      className={tableStyles.pageContainer}
      data-test-id="table-page-container"
    >
      <div className={tableStyles.tableToolbar}>
        <TableTitle
          title={i18next.t('title')}
          count={resourcesQuery.data?.totalCount}
        />
        <Button
          testId="plus-button"
          title={i18next.t('add_button')}
          color={ButtonColor.Primary}
          onClick={() => openCreating(props.resourceType)}
          iconComponent={<AddIcon />}
        />
      </div>
      <div className={tableStyles.tableContainer}>
        <Loading loading={!resourcesQuery.data}>
          <ResourcesTable
            data={resourcesQuery.data?.data ?? []}
            noResults={
              !!resourcesQuery.data && resourcesQuery.data.totalCount === 0
            }
            onDelete={removeResource}
            onEdit={openEditing}
            translationScope={translationScope}
          />
        </Loading>
      </div>
    </div>
  );
};

export default ResourceList;
